/**
 * ForumExpressPay — embedded Apple Pay / Google Pay button for the AED 149
 * Invest UAE Forum 2026 ticket. Sits ABOVE the existing "Pay AED 149 → Stripe Checkout"
 * fallback button so mobile wallet users complete the purchase 100% on skiaxis.com.
 *
 * Flow:
 *  1. Component mounts → POST /api/payments/forum-ticket/payment-intent
 *  2. Backend creates Stripe PaymentIntent (AED 149, fixed server-side) → returns client_secret
 *  3. <Elements stripe={stripePromise} options={{ clientSecret }}> wraps the button
 *  4. <ExpressCheckoutElement> renders Apple Pay / Google Pay only on devices that support them
 *  5. User taps wallet → Stripe handles native sheet → confirmPayment with return_url
 *  6. Stripe redirects to /forum/thank-you?payment_intent=pi_xxx&payment_intent_client_secret=...
 *  7. ThankYou page polls /api/payments/payment-intent/status/{pi_xxx} until paid
 *
 * If wallet support isn't detected (desktop / no saved cards), the component renders
 * nothing — the existing fallback "Pay AED 149 → Stripe Checkout" button is used instead.
 */
import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

let _stripePromise = null;
const getStripe = () => {
  if (!_stripePromise && PUBLISHABLE_KEY) {
    _stripePromise = loadStripe(PUBLISHABLE_KEY);
  }
  return _stripePromise;
};

function InnerExpressPay({ guestMode, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [walletAvailable, setWalletAvailable] = useState(null); // null=unknown, true=show, false=hide
  const [processing, setProcessing] = useState(false);

  const onConfirm = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError?.(submitError.message || 'Wallet payment cancelled.');
        setProcessing(false);
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/forum/thank-you`,
        },
      });
      // If we get here, the payment failed (otherwise the page already redirected).
      if (error) onError?.(error.message || 'Payment failed.');
    } catch (e) {
      onError?.(e?.message || 'Payment error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="forum-express-pay-container">
      <ExpressCheckoutElement
        onConfirm={onConfirm}
        onReady={(event) => {
          // event.availablePaymentMethods is { applePay, googlePay, link, paypal }
          const m = event?.availablePaymentMethods || {};
          const has = !!(m.applePay || m.googlePay || m.link);
          setWalletAvailable(has);
        }}
        options={{
          // applePay valid: plain | buy | check-out | book | donate | subscribe | reload | add-money | top-up | order | rent | support | contribute | tip
          // googlePay valid: plain | buy | book | checkout | donate | order | pay | subscribe
          buttonType: { applePay: 'check-out', googlePay: 'checkout' },
          buttonHeight: 48,
          paymentMethods: {
            applePay: 'auto',
            googlePay: 'auto',
            link: 'never',
            paypal: 'never',
            amazonPay: 'never',
          },
          // In guest mode (no form filled), require the wallet sheet to collect email + name.
          // Stripe passes them back via billing_details on the charge — backend backfills them.
          ...(guestMode ? {
            emailRequired: true,
            phoneNumberRequired: false,
            billingAddressRequired: 'auto',
          } : {}),
        }}
      />
      {walletAvailable === false && (
        <div style={{ fontSize: 11, color: 'rgba(13,27,42,.55)', textAlign: 'center', marginTop: 6, fontStyle: 'italic' }}>
          Apple Pay / Google Pay not detected on this device — use the card option below.
        </div>
      )}
      {processing && (
        <div style={{ fontSize: 11, color: 'var(--gold)', textAlign: 'center', marginTop: 8, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700 }}>
          Processing payment…
        </div>
      )}
    </div>
  );
}

export default function ForumExpressPay({ email, fullName, registrationId, onError, guestMode = false, source = 'modal' }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [bootError, setBootError] = useState(null);
  const bootedRef = useRef(null);  // guards against React StrictMode double-fire

  useEffect(() => {
    if (!PUBLISHABLE_KEY) {
      // Silently disabled if key not configured
      return;
    }
    // In NORMAL mode: must have a valid email before firing PaymentIntent (avoids orphan intents).
    // In GUEST mode (floating bar): create the PaymentIntent immediately with no email — wallet collects it.
    if (!guestMode && (!email || !email.includes('@'))) {
      return;
    }
    // Guard against double-fire (StrictMode + dependency churn). One PaymentIntent per email/guestMode.
    const bootKey = guestMode ? `__guest__${source}` : email;
    if (bootedRef.current === bootKey) {
      return;
    }
    bootedRef.current = bootKey;
    let cancelled = false;
    (async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const testMode = urlParams.get('test') === '1' || localStorage.getItem('stripe_test_mode') === '1';
        const url = `${API}/payments/forum-ticket/payment-intent${testMode ? '?test=1' : ''}`;
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email || null,
            full_name: fullName || null,
            registration_id: registrationId || null,
            source: source || 'modal',
          }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled && data?.client_secret) setClientSecret(data.client_secret);
      } catch (e) {
        if (!cancelled) setBootError(e?.message || 'Could not initialise wallet payment');
      }
    })();
    return () => { cancelled = true; };
  }, [email, fullName, registrationId, guestMode, source]);

  // Hide entirely if no key configured or boot failed — let the fallback Stripe Checkout button show
  if (!PUBLISHABLE_KEY || bootError) return null;
  if (!clientSecret) {
    return (
      <div style={{ fontSize: 11, color: 'rgba(13,27,42,.55)', textAlign: 'center', padding: '8px 0', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
        Loading wallet payment…
      </div>
    );
  }

  const stripePromise = getStripe();
  if (!stripePromise) return null;

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'flat',
      variables: {
        colorPrimary: '#C8A84B',
        colorBackground: '#FFFFFF',
        colorText: '#0D1B2A',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        borderRadius: '6px',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <InnerExpressPay
        guestMode={guestMode}
        onError={onError}
      />
    </Elements>
  );
}
